import Decimal from "decimal.js";
import moment from "moment-timezone";
import type { ContractMatchProposalResult } from "@/lib/api/contractMatching";
import { CommonDestinationTimezone } from "@/lib/utils";

export type RankableContractCandidate = {
  clientId: string;
  commodity: string;
  quantity: string | null;
  contractDate: string | null;
  contractType: string;
};

export function parseContractCalendarDate(value: string | null): moment.Moment | null {
  if (!value) return null;
  const isTimeStamped = value.includes("T");
  const isUtcTimeStamped = isTimeStamped && value.split("T")[1].startsWith("00:00:00");
  const isCommonTimeStamped = isTimeStamped && !isUtcTimeStamped;

  const calendarDate = isUtcTimeStamped
    ? moment.utc(value).format("YYYY-MM-DD")
    : isCommonTimeStamped
    ? moment.tz(value, CommonDestinationTimezone).format("YYYY-MM-DD")
    : value;
  if (isTimeStamped && !isUtcTimeStamped) {
    console.warn("found unexpected date format", "value", value, isTimeStamped, isUtcTimeStamped, isCommonTimeStamped, calendarDate)
  }

  // NOTE: Doesnt parse to common because its already YYYY-MM-DD and we want to compare dates in the current timezone
  // Since both are being formatted as if YYYY-MM-DD
  const parsedDate = moment(
    calendarDate,
    "YYYY-MM-DD",
    true,
  );
  return parsedDate.isValid() ? parsedDate : null;
}

export function formatContractCalendarDate(value: string | null): string {
  return parseContractCalendarDate(value)?.format("MM/DD/YYYY") ?? "";
}

function contractDateDistance(
  candidateDate: string | null,
  targetDate: string | null,
): Decimal | null {
  const parsedCandidateDate = parseContractCalendarDate(candidateDate);
  const parsedTargetDate = parseContractCalendarDate(targetDate);
  if (!parsedCandidateDate || !parsedTargetDate) return null;
  return new Decimal(parsedCandidateDate.diff(parsedTargetDate, "days")).abs();
}

function quantityDistance(
  candidateQuantity: string | null,
  targetQuantity: string | null,
): Decimal | null {
  if (!candidateQuantity || !targetQuantity) return null;
  try {
    return new Decimal(candidateQuantity).minus(targetQuantity).abs();
  } catch {
    return null;
  }
}

function compareDistances(first: Decimal | null, second: Decimal | null): number {
  if (first && second) return first.comparedTo(second);
  if (first) return -1;
  if (second) return 1;
  return 0;
}

export function rankCandidateRows<TCandidate extends RankableContractCandidate>(
  candidates: TCandidate[],
  proposal: ContractMatchProposalResult,
): TCandidate[] {
  const targetClientId = proposal.target.Customer?.client_id
    ?? proposal.target.primary.customer_client_id;
  const targetCommodity = proposal.target.primary.commodity;
  const targetContractDate = proposal.target.primary.contract_date;
  const targetQuantity = proposal.target.primary.quantity;

  return candidates.sort(
    (first: TCandidate, second: TCandidate): number => {
      const firstIsOtc = /^otc/i.test(first.contractType);
      const secondIsOtc = /^otc/i.test(second.contractType);
      if (firstIsOtc !== secondIsOtc) {
        const shouldPrioritizeOtc = proposal.target.target_type === "trading_contract";
        return firstIsOtc === shouldPrioritizeOtc ? -1 : 1;
      }

      const firstCommodityMatches = first.commodity === targetCommodity;
      const secondCommodityMatches = second.commodity === targetCommodity;
      if (firstCommodityMatches !== secondCommodityMatches) {
        return firstCommodityMatches ? -1 : 1;
      }

      const firstCustomerMatches = targetClientId !== null
        && first.clientId === targetClientId;
      const secondCustomerMatches = targetClientId !== null
        && second.clientId === targetClientId;
      if (firstCustomerMatches !== secondCustomerMatches) {
        return firstCustomerMatches ? -1 : 1;
      }

      const dateComparison = compareDistances(
        contractDateDistance(first.contractDate, targetContractDate),
        contractDateDistance(second.contractDate, targetContractDate),
      );
      if (dateComparison !== 0) return dateComparison;

      return compareDistances(
        quantityDistance(first.quantity, targetQuantity),
        quantityDistance(second.quantity, targetQuantity),
      );
    },
  );
}
